import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { validateGrant, safeId } from './supportSessionClient';
const Context=createContext(null);
export const useSupportSession=()=>useContext(Context);
export function SupportSessionProvider({actor,client,children}) {
 const [state,setState]=useState({status:'idle',metadata:null});
 const stateRef=useRef(state); const secret=useRef(null); const generation=useRef(0); const pending=useRef(new Set());
 const update=useCallback(next=>{stateRef.current=next;setState(next);},[]);
 const clear=useCallback(()=>{ secret.current=null; generation.current++; pending.current.forEach(c=>c.abort());pending.current.clear(); },[]);
 const block=useCallback(status=>{clear();update({...stateRef.current,status});},[clear,update]);
 useEffect(()=>{clear();update({status:'idle',metadata:null});return clear;},[actor?.user_id,actor?.role,clear,update]);
 const read=useCallback(async resource=>{
  if(stateRef.current.status!=='active' || !secret.current) throw new Error('Support session required');
  if(Date.now()>=Date.parse(stateRef.current.metadata.session.expires_at)){block('expired');throw new Error('Local access cleared; reconcile server status');}
  const epoch=generation.current; const controller=new AbortController();pending.current.add(controller);
  try {const data=await client.read(resource,secret.current,controller.signal);
   if(epoch!==generation.current) throw new Error('Stale support response');
   const m=stateRef.current.metadata; const checked=validateGrant(data,actor.user_id,m.organization.org_id,false);
   if(checked.session.support_session_id!==m.session.support_session_id) throw new Error('Session changed');
   return data;
  }catch {if(epoch===generation.current) block('blocked');throw new Error('Support data unavailable');}
  finally{pending.current.delete(controller);}
 },[actor?.user_id,client,block]);
 useEffect(()=>{
  if(state.status!=='active') return undefined;
  const timer=setInterval(()=>{if(Date.now()>=Date.parse(stateRef.current.metadata.session.expires_at))block('expired');},1000);
  const visible=()=>{if(document.visibilityState==='visible')read('context').catch(()=>{});};
  document.addEventListener('visibilitychange',visible);
  return ()=>{clearInterval(timer);document.removeEventListener('visibilitychange',visible);};
 },[state.status,read,block]);
 const start=async(orgId,reason,password)=>{
  if(!['idle','ended'].includes(stateRef.current.status) || actor?.role!=='super_admin') throw new Error('Reconcile existing support session first');
  if(!safeId(orgId)||reason.trim().length<10||reason.length>500||!password||password.length>200)throw new Error('Reason and password required');
  clear(); const epoch=generation.current;update({status:'starting',metadata:null,targetId:orgId});
  try {const data=await client.start(orgId,reason.trim(),password);if(epoch!==generation.current)throw new Error('Stale start');
   const metadata=validateGrant(data,actor.user_id,orgId);update({status:'authorizing',metadata});secret.current=data.credential;
   const checked=await client.read('context',secret.current);validateGrant(checked,actor.user_id,orgId,false);
   if(epoch!==generation.current||checked.session.support_session_id!==metadata.session.support_session_id)throw new Error('Stale context');
   update({status:'active',metadata});return metadata;
  }catch{if(epoch===generation.current)block('unknown');throw new Error('Start not confirmed. Reconcile before trying again.');}
 };
 const reconcile=async()=>{
  block('reconciling'); const epoch=generation.current;
  try{const data=await client.current();if(epoch!==generation.current)return;
   if(!data?.session){update({...stateRef.current,status:'unknown'});return;}
   const metadata=validateGrant(data,actor.user_id,stateRef.current.targetId||stateRef.current.metadata?.organization.org_id||data.organization?.org_id,false);
   update({status:'recovered',metadata});
  }catch{if(epoch===generation.current)update({...stateRef.current,status:'unknown'});}
 };
 const exit=async()=>{
  const m=stateRef.current.metadata;if(!m)return reconcile();
  block('exitPending'); const epoch=generation.current;
  try{const data=await client.exit(m.session.support_session_id);
   if(epoch!==generation.current)return;
   if(data?.support_session_id!==m.session.support_session_id||!['revoked','expired'].includes(data.status))throw new Error('Exit not confirmed');
   update({status:'ended',metadata:null,returnPath:`/platform/organizations/${m.organization.org_id}`});
  }catch{if(epoch===generation.current)update({...stateRef.current,status:'exitFailed'});throw new Error('Exit not confirmed');}
 };
 return <Context.Provider value={{...state,start,read,exit,reconcile}}>{children}</Context.Provider>;
}
